import * as React from "react";

import { useTriggerEvaluationCount } from "./useTriggerEvaluationCount";
import { useTriggerEvaluationsQuery } from "./useTriggerEvaluationsQuery";

/**
 * Hook to manage pagination state for trigger evaluations
 */
export function useTriggerEvaluationsPagination(filter: { datasetId: string }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);

  const evaluationsQuery = useTriggerEvaluationsQuery({
    filter,
    limit: rowsPerPage,
  });

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
    evaluationsQuery;

  const countQuery = useTriggerEvaluationCount({
    datasetId: filter.datasetId,
  });

  React.useEffect(() => {
    setPage(0);
  }, [filter.datasetId]);

  React.useEffect(() => {
    const totalItemsNeeded = (page + 1) * rowsPerPage;
    const currentItemsCount = data?.length ?? 0;

    if (
      totalItemsNeeded > currentItemsCount &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      void fetchNextPage();
    }
  }, [
    page,
    rowsPerPage,
    data?.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return {
    page,
    rowsPerPage,
    evaluationsQuery,
    countQuery,
    handlePageChange,
    handleRowsPerPageChange,
  };
}
